import { useState, useEffect, useCallback } from "react";
import MainLayout from "../../layouts/MainLayout";
import "./ConsultationReport.css";
import { BASE_URL } from "../../pages/Config";

/* ─── API endpoints ──────────────────────────────────────── */

const REPORT_API = `${BASE_URL}api/reporting/consultation-report`;
const SOURCE_API = `${BASE_URL}api/Reporting/source`;

/* ─── Helpers ────────────────────────────────────────────── */
function getToday() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmt(t) {
    if (!t) return "—";
    return new Date(t).toLocaleString("en-PK", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit", hour12: true,
    });
}

function getPages(cur, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (cur <= 4)         return [1, 2, 3, 4, 5, "…", total];
    if (cur >= total - 3) return [1, "…", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "…", cur - 1, cur, cur + 1, "…", total];
}

const CONSULT_TYPE_STYLE = {
    Phone:  { bg: "#eff6ff", color: "#1d4ed8" },
    Video:  { bg: "#f0fdf4", color: "#15803d" },
    Chat:   { bg: "#fdf4ff", color: "#7e22ce" },
};

function TypeBadge({ type }) {
    const s = CONSULT_TYPE_STYLE[type] || { bg: "#f1f5f9", color: "#475569" };
    return (
        <span className="cr-type-badge" style={{ background: s.bg, color: s.color }}>
            {type || "—"}
        </span>
    );
}

function Avatar({ name }) {
    const initials = (name || "?")
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join("");
    return <span className="cr-avatar">{initials}</span>;
}

/* ─── Main component ─────────────────────────────────────── */
export default function ConsultationReport() {
    /* filters */
    const [source, setSource]         = useState("");
    const [fromDate, setFromDate]     = useState(getToday);
    const [toDate, setToDate]         = useState(getToday);
    const [emailInput, setEmailInput] = useState("");
    const [email, setEmail]           = useState("");

    /* pagination */
    const [page, setPage]         = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [totalRecs, setTotalRecs] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    /* data */
    const [rows, setRows]       = useState([]);
    const [loading, setLoading] = useState(false);
    const [apiError, setApiError] = useState(false);
    const [searched, setSearched] = useState(false);

    /* sources dropdown */
    const [sources, setSources] = useState([]);

    /* load sources once */
    useEffect(() => {
        fetch(SOURCE_API)
            .then(r => r.ok ? r.json() : null)
            .then(j => setSources(j?.data || []))
            .catch(() => setSources([]));
    }, []);

    const fetchReport = useCallback(async (src, fd, td, em, pg, ps) => {
        setLoading(true);
        setApiError(false);
        setSearched(true);
        try {
            const res = await fetch(REPORT_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    source: src || null,
                    fromDate: fd || null,
                    toDate: td || null,
                    email: em || null,
                    pageNumber: pg,
                    pageSize: ps,
                    page: pg,
                }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json();
            const allRows = j.data || [];
            const total = j.totalRecords ?? j.count ?? allRows.length;
            const pages = j.totalPages ?? Math.max(1, Math.ceil(total / ps) || 1);

            // Server-paginated → use rows as-is; else client-side slice
            const pageRows =
                j.totalPages != null || j.totalRecords != null || allRows.length <= ps
                    ? allRows
                    : allRows.slice((pg - 1) * ps, pg * ps);

            setRows(pageRows);
            setTotalRecs(total);
            setTotalPages(pages);
        } catch {
            setApiError(true);
            setRows([]);
            setTotalRecs(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, []);

    /* auto-fetch on mount, filter & page change */
    useEffect(() => {
        fetchReport(source, fromDate, toDate, email, page, pageSize);
    }, [source, fromDate, toDate, email, page, pageSize, fetchReport]);

    const doSearch = () => {
        setPage(1);
        setEmail(emailInput);
    };

    const clearAll = () => {
        setSource("");
        setFromDate(getToday());
        setToDate(getToday());
        setEmailInput("");
        setEmail("");
        setPage(1);
    };

    const hasActive = source || email;
    const start = totalRecs === 0 ? 0 : (page - 1) * pageSize + 1;
    const end = Math.min(page * pageSize, totalRecs);

    const downloadCSV = () => {
        if (!rows.length) return;
        const headers = ["ID", "Patient Name", "Mobile", "Email", "Doctor", "Source", "Type", "Complaint", "Diagnosis", "Consultation Date"];
        const csvRows = [
            headers.join(","),
            ...rows.map(r => [
                r.c?.id,
                r.patientName,
                r.mobileNumber,
                r.email,
                r.doctorName,
                r.source,
                r.c?.consultationType,
                r.c?.complaint,
                r.c?.diagnosis,
                fmt(r.c?.consultationDate),
            ].map(v => `"${(v ?? "").toString().replace(/"/g, '""')}"`).join(","))
        ];
        const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.setAttribute("download", `consultation-report-${Date.now()}.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <MainLayout>
            <div className="cr-page">

                {/* ── Page header ── */}
                <div className="cr-page-header">
                    <div>
                        <p className="cr-eyebrow">Reporting</p>
                        <h1 className="cr-title">Consultation Report</h1>
                    </div>
                    <button
                        className="cr-btn-refresh"
                        onClick={() => fetchReport(source, fromDate, toDate, email, page, pageSize)}
                    >
                        ↻ Refresh
                    </button>
                </div>

                {/* ── Filters card ── */}
                <div className="cr-card">
                    <div className="cr-filter-grid">

                        <div className="cr-filter-group">
                            <label className="cr-label">Source</label>
                            <select
                                className="cr-sel"
                                value={source}
                                onChange={e => { setSource(e.target.value); setPage(1); }}
                            >
                                <option value="">All Sources</option>
                                {sources.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        </div>

                        <div className="cr-filter-group">
                            <label className="cr-label">From</label>
                            <input
                                className="cr-inp"
                                type="date"
                                value={fromDate}
                                onChange={e => { setFromDate(e.target.value); setPage(1); }}
                            />
                        </div>

                        <div className="cr-filter-group">
                            <label className="cr-label">To</label>
                            <input
                                className="cr-inp"
                                type="date"
                                value={toDate}
                                onChange={e => { setToDate(e.target.value); setPage(1); }}
                            />
                        </div>

                        <div className="cr-filter-group cr-span-2">
                            <label className="cr-label">Search by mobile / email</label>
                            <div className="cr-search-row">
                                <input
                                    className="cr-inp"
                                    type="text"
                                    placeholder="e.g. 03012345678"
                                    value={emailInput}
                                    onChange={e => setEmailInput(e.target.value)}
                                    onKeyDown={e => e.key === "Enter" && doSearch()}
                                />
                                <button className="cr-btn-search" onClick={doSearch}>
                                    🔍 Search
                                </button>
                                <button className="cr-btn-export" onClick={downloadCSV}>
                                    ⬇ Export CSV
                                </button>
                                {hasActive && (
                                    <button className="cr-btn-clear" onClick={clearAll}>
                                        ✕ Clear
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>
                </div>

                {/* ── API warning ── */}
                {apiError && (
                    <div className="cr-warn">
                        ⚠️ Unable to load consultation report. Please try again.
                    </div>
                )}

                {/* ── Results card ── */}
                <div className="cr-card">
                    <div className="cr-toolbar">
                        <span className="cr-rec-txt">
                            {loading
                                ? "Loading…"
                                : !searched
                                    ? "Enter filters to search"
                                    : totalRecs === 0
                                        ? "No records found"
                                        : `Showing ${start.toLocaleString()} – ${end.toLocaleString()} of ${totalRecs.toLocaleString()} records`}
                        </span>
                        <div className="cr-toolbar-right">
                            <span className="cr-rows-label">Rows / page</span>
                            <select
                                className="cr-sel cr-sel-sm"
                                value={pageSize}
                                onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                            >
                                {[10, 20, 50, 100].map(n => (
                                    <option key={n} value={n}>{n}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="cr-table-wrap">
                        <table className="cr-table">
                            <thead>
                                <tr>
                                    {["#", "Patient", "Mobile", "Doctor", "Source", "Type", "Complaint", "Diagnosis", "Date"].map(h => (
                                        <th key={h}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={9} className="cr-cell-center">
                                            <div className="cr-loader">
                                                <div className="cr-spinner" />
                                                <span>Fetching records…</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : rows.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="cr-cell-center">
                                            <div className="cr-empty">
                                                <div className="cr-empty-icon">🗂️</div>
                                                <p>No consultations match the selected filters.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : rows.map((r, i) => (
                                    <tr key={r.c?.id ?? `${page}-${i}`} className="cr-row">
                                        <td className="cr-id">{r.c?.id ?? "—"}</td>
                                        <td>
                                            <div className="cr-patient-cell">
                                                <Avatar name={r.patientName} />
                                                <div className="cr-patient-info">
                                                    <span className="cr-patient-name">{r.patientName || "—"}</span>
                                                    <span className="cr-patient-email">{r.email || "—"}</span>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="cr-mono cr-mobile">{r.mobileNumber || "—"}</td>
                                        <td className="cr-doctor">{r.doctorName || "—"}</td>
                                        <td>
                                            <span className="cr-source-tag">{r.source || "—"}</span>
                                        </td>
                                        <td><TypeBadge type={r.c?.consultationType} /></td>
                                        <td className="cr-wrap-cell">{r.c?.complaint || "—"}</td>
                                        <td className="cr-wrap-cell">{r.c?.diagnosis || "—"}</td>
                                        <td className="cr-datetime">{fmt(r.c?.consultationDate)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    {totalRecs > 0 && (
                        <div className="cr-pg-row">
                            <span className="cr-pg-info">
                                Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                            </span>

                            <div className="cr-pg-btns">
                                <button
                                    className="cr-pg-btn"
                                    disabled={page === 1}
                                    onClick={() => setPage(1)}
                                    title="First"
                                >
                                    «
                                </button>
                                <button
                                    className="cr-pg-btn"
                                    disabled={page === 1}
                                    onClick={() => setPage(p => p - 1)}
                                    title="Prev"
                                >
                                    ‹
                                </button>

                                {getPages(page, totalPages).map((p, i) =>
                                    p === "…" ? (
                                        <span key={`e${i}`} className="cr-pg-ellipsis">…</span>
                                    ) : (
                                        <button
                                            key={p}
                                            className={`cr-pg-btn${p === page ? " cr-pg-active" : ""}`}
                                            onClick={() => setPage(p)}
                                        >
                                            {p}
                                        </button>
                                    )
                                )}

                                <button
                                    className="cr-pg-btn"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(p => p + 1)}
                                    title="Next"
                                >
                                    ›
                                </button>
                                <button
                                    className="cr-pg-btn"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(totalPages)}
                                    title="Last"
                                >
                                    »
                                </button>
                            </div>
                        </div>
                    )}
                </div>

            </div>
        </MainLayout>
    );
}
